/** Immutable object/list operations shared by the interpreter and compiler. */
const unsafe = new Set(["__proto__", "prototype", "constructor"]);
const record = (value: unknown): Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
export function extendObject(
  base: unknown,
  additions: unknown,
): Record<string, unknown> {
  return Object.fromEntries(
    [
      ...Object.entries(record(base)),
      ...Object.entries(record(additions)),
    ].filter(([key]) => !unsafe.has(key)),
  );
}
export function setObjectProperty(
  base: unknown,
  path: unknown,
  value: unknown,
): Record<string, unknown> {
  const keys = String(path ?? "").split(".");
  if (keys.some((key) => !key || unsafe.has(key)))
    return extendObject(base, {});
  const [first, ...rest] = keys;
  return extendObject(base, {
    [first]: rest.length
      ? setObjectProperty(record(base)[first], rest.join("."), value)
      : value,
  });
}
export function selectObjectProperties(
  base: unknown,
  keys: unknown,
  omit = false,
): Record<string, unknown> {
  const selected = new Set(
    Array.isArray(keys)
      ? keys.map(String)
      : String(keys ?? "")
          .split(",")
          .map((k) => k.trim()),
  );
  return Object.fromEntries(
    Object.entries(record(base)).filter(
      ([key]) => !unsafe.has(key) && selected.has(key) !== omit,
    ),
  );
}
export function constructObject(
  base: unknown,
  fields: Array<[string, unknown]>,
): Record<string, unknown> {
  return extendObject(
    base,
    Object.fromEntries(
      fields.filter(([key, value]) => !unsafe.has(key) && value !== undefined),
    ),
  );
}
export function mapObjectValues(
  base: unknown,
  transform: (value: unknown, key: string, index: number) => unknown,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(record(base))
      .filter(([key]) => !unsafe.has(key))
      .map(([key, value], index) => [key, transform(value, key, index)]),
  );
}
export function objectEntries(
  base: unknown,
): Array<{ key: string; value: unknown }> {
  return Object.entries(record(base))
    .filter(([key]) => !unsafe.has(key))
    .map(([key, value]) => ({ key, value }));
}
